import React, { useState, useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  IconButton,
  Box,
  Link,
  LinearProgress,
  Tooltip,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  InsertDriveFile as FileIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import {
  DOCUMENT_CATEGORY_LABELS,
  DOCUMENT_SLOTS,
  COMPLETE_CATEGORY_PDF_SLOT,
} from '../../config/constants';
import { uploadDocuments, mergeCategory, downloadDocument, viewDocument, deleteDocument, uploadCompleteCategoryPdf } from '../../services/documentService';
import usePermissions from '../../hooks/usePermissions';
import MergeButton from './MergeButton';
import DocumentPreview from './DocumentPreview';

const DocumentCategoryCard = ({
  studentId,
  category,
  categoryData = {},
  documents = [],
  onRefresh,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [uploading, setUploading] = useState({});
  const [merging, setMerging] = useState(false);
  const [addableCount, setAddableCount] = useState(null);
  const [error, setError] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [uploadingCompletePdf, setUploadingCompletePdf] = useState(false);
  const fileInputRefs = useRef({});
  const completePdfInputRef = useRef(null);
  const { canUploadDocuments, canMergeDocuments } = usePermissions();

  // Separate complete category PDF from individual slot documents
  const completePdfDoc = documents.find((d) => d.slotName === COMPLETE_CATEGORY_PDF_SLOT);
  const slotDocuments = documents.filter((d) => d.slotName !== COMPLETE_CATEGORY_PDF_SLOT);

  const label = DOCUMENT_CATEGORY_LABELS[category] || category;
  const isExtraCategory = category === 'extra_documents';
  const isAddableCategory = category === 'semester_memos';
  const predefinedSlots = DOCUMENT_SLOTS[category] || [];

  // Map documents by slotName for easy lookup
  const docsBySlot = {};
  slotDocuments.forEach((doc) => {
    if (doc.slotName) {
      docsBySlot[doc.slotName] = doc;
    }
  });

  // Build slots based on category type
  let slots;
  if (isExtraCategory) {
    slots = slotDocuments.map((doc) => ({ key: doc.slotName || doc._id, label: doc.slotName || doc.originalName }));
  } else if (isAddableCategory) {
    const uploadedKeys = Object.keys(docsBySlot).filter((k) => k.startsWith('sem_memo_'));
    const maxUploaded = uploadedKeys.reduce((max, k) => {
      const n = parseInt(k.replace('sem_memo_', ''), 10);
      return n > max ? n : max;
    }, 0);
    const count = addableCount !== null ? addableCount : Math.max(maxUploaded, 1);
    if (addableCount === null && count > 0) setAddableCount(count);
    slots = [];
    for (let i = 1; i <= count; i++) {
      slots.push({ key: `sem_memo_${i}`, label: `Semester Memo ${i}` });
    }
  } else {
    slots = predefinedSlots;
  }

  const uploadedCount = isExtraCategory ? slotDocuments.length : slots.filter((s) => docsBySlot[s.key]).length;
  const totalSlots = isExtraCategory ? slotDocuments.length : slots.length;
  const hasCompletePdf = !!completePdfDoc;
  const allUploaded = hasCompletePdf || (isExtraCategory ? slotDocuments.length > 0 : (uploadedCount === totalSlots && totalSlots > 0));

  const handleFileSelect = async (slotKey, event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError(null);
    setUploading((prev) => ({ ...prev, [slotKey]: true }));

    try {
      await uploadDocuments(studentId, category, [file], slotKey);
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(`Failed to upload: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploading((prev) => ({ ...prev, [slotKey]: false }));
      // Reset file input
      if (fileInputRefs.current[slotKey]) {
        fileInputRefs.current[slotKey].value = '';
      }
    }
  };

  const handleView = async (doc) => {
    try {
      const response = await viewDocument(doc._id);
      const blob = new Blob([response.data], { type: doc.mimeType || 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      console.error('View failed:', err);
    }
  };

  const handleClosePreview = () => {
    if (previewBlobUrl) window.URL.revokeObjectURL(previewBlobUrl);
    setPreviewDoc(null);
    setPreviewBlobUrl(null);
  };

  const handleDownload = async (doc) => {
    try {
      const response = await downloadDocument(doc._id);
      const blob = new Blob([response.data], { type: doc.mimeType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.originalName}"?`)) return;
    try {
      await deleteDocument(doc._id);
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleMerge = async () => {
    setMerging(true);
    try {
      await mergeCategory(studentId, category);
      if (onRefresh) onRefresh();
    } catch (err) {
      setError('Merge failed. Please try again.');
    } finally {
      setMerging(false);
    }
  };

  const handleCompletePdfSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are accepted for complete category upload.');
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      setError('File too large. Maximum size is 100 MB.');
      return;
    }
    setError(null);
    setUploadingCompletePdf(true);
    try {
      await uploadCompleteCategoryPdf(studentId, category, file);
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(`Failed to upload: ${err.response?.data?.message || err.message}`);
    } finally {
      setUploadingCompletePdf(false);
      if (completePdfInputRef.current) completePdfInputRef.current.value = '';
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 2 },
      }}
    >
      {/* Header - always visible */}
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {label}
          </Typography>
          <Chip
            label={
              hasCompletePdf && uploadedCount === 0
                ? 'PDF Uploaded'
                : isExtraCategory
                ? `${slotDocuments.length} file${slotDocuments.length !== 1 ? 's' : ''}`
                : `${uploadedCount}/${totalSlots}`
            }
            size="small"
            color={allUploaded ? 'success' : uploadedCount > 0 ? 'warning' : 'default'}
            variant={allUploaded ? 'filled' : 'outlined'}
            sx={{ fontWeight: 600, fontSize: '0.75rem' }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {categoryData?.mergedPdfUrl && (
            <Tooltip title="Download Merged PDF">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(categoryData.mergedPdfUrl, '_blank');
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Box>
      </Box>

      {/* Progress bar */}
      {(isExtraCategory ? documents.length > 0 : totalSlots > 0) && (
        <LinearProgress
          variant="determinate"
          value={hasCompletePdf ? 100 : isExtraCategory ? 100 : (uploadedCount / totalSlots) * 100}
          sx={{
            height: 3,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              backgroundColor: allUploaded ? 'success.main' : 'warning.main',
            },
          }}
        />
      )}

      {/* Expanded content - slot list */}
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 1, pb: 2 }}>
          {error && (
            <Alert severity="error" size="small" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Slot list */}
          {slots.map((slot) => {
            const doc = docsBySlot[slot.key];
            const isUploading = uploading[slot.key];

            return (
              <Box
                key={slot.key}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                  '&:hover': { backgroundColor: 'action.hover' },
                  borderRadius: 1,
                }}
              >
                {/* Left side: status icon + slot name */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  {doc ? (
                    <CheckIcon color="success" sx={{ fontSize: 20 }} />
                  ) : (
                    <PendingIcon color="disabled" sx={{ fontSize: 20 }} />
                  )}
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={doc ? 600 : 400}
                      color={doc ? 'text.primary' : 'text.secondary'}
                      noWrap
                    >
                      {slot.label}
                    </Typography>
                    {doc && (
                      <Typography variant="caption" color="text.disabled" noWrap>
                        {doc.originalName}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Right side: actions */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                  {isUploading ? (
                    <Box sx={{ width: 80 }}>
                      <LinearProgress size="small" />
                    </Box>
                  ) : doc ? (
                    <>
                      <Tooltip title="View">
                        <IconButton size="small" color="info" onClick={() => handleView(doc)}>
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download">
                        <IconButton size="small" onClick={() => handleDownload(doc)}>
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {canUploadDocuments && (
                        <Tooltip title="Replace">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => fileInputRefs.current[slot.key]?.click()}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canUploadDocuments && (
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(doc)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  ) : canUploadDocuments ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => fileInputRefs.current[slot.key]?.click()}
                      sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                    >
                      Upload
                    </Button>
                  ) : (
                    <Chip label="Pending" size="small" variant="outlined" color="default" />
                  )}

                  {/* Hidden file input for this slot */}
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                    ref={(el) => (fileInputRefs.current[slot.key] = el)}
                    onChange={(e) => handleFileSelect(slot.key, e)}
                  />
                </Box>
              </Box>
            );
          })}

          {/* Add Another button for addable categories */}
          {isAddableCategory && canUploadDocuments && (
            <Box sx={{ px: 1, py: 1.5 }}>
              <Button
                size="small"
                variant="text"
                startIcon={<UploadIcon />}
                onClick={() => setAddableCount((prev) => (prev || 1) + 1)}
                sx={{ textTransform: 'none', fontSize: '0.8rem', fontWeight: 600 }}
              >
                + Add New Semester Memo
              </Button>
            </Box>
          )}

          {/* Complete Category PDF section */}
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, textAlign: 'center' }}>
              Or upload a single PDF for all {label} documents
            </Typography>

            {completePdfDoc ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 1,
                  px: 1.5,
                  backgroundColor: 'primary.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.200',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                  <PdfIcon color="error" sx={{ fontSize: 22 }} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      Complete {label} PDF
                    </Typography>
                    <Typography variant="caption" color="text.disabled" noWrap>
                      {completePdfDoc.originalName}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                  <Tooltip title="View">
                    <IconButton size="small" color="info" onClick={() => handleView(completePdfDoc)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download">
                    <IconButton size="small" onClick={() => handleDownload(completePdfDoc)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {canUploadDocuments && (
                    <Tooltip title="Replace">
                      <IconButton size="small" color="primary" onClick={() => completePdfInputRef.current?.click()}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canUploadDocuments && (
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(completePdfDoc)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            ) : canUploadDocuments ? (
              <Box sx={{ textAlign: 'center' }}>
                {uploadingCompletePdf ? (
                  <LinearProgress sx={{ mx: 4, my: 1 }} />
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<PdfIcon />}
                    onClick={() => completePdfInputRef.current?.click()}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Upload Complete PDF
                  </Button>
                )}
              </Box>
            ) : null}

            <input
              type="file"
              accept=".pdf"
              style={{ display: 'none' }}
              ref={completePdfInputRef}
              onChange={handleCompletePdfSelect}
            />
          </Box>

          {/* Merge button */}
          {canMergeDocuments && uploadedCount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
              <MergeButton
                onClick={handleMerge}
                loading={merging}
                label="Convert to PDF"
                size="small"
              />
            </Box>
          )}
        </CardContent>
      </Collapse>

      {/* Preview popup */}
      <DocumentPreview
        open={!!previewDoc}
        onClose={handleClosePreview}
        blobUrl={previewBlobUrl}
        fileName={previewDoc?.originalName}
        mimeType={previewDoc?.mimeType}
        onDownload={previewDoc ? () => handleDownload(previewDoc) : undefined}
      />
    </Card>
  );
};

export default DocumentCategoryCard;
